import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { Toaster } from 'react-hot-toast';
import Chat from './pages/Chat';
import Dashboard from './pages/Dashboard';
import Login from './pages/Login';
import AccountSelection from './pages/AccountSelection';
import CreativeReport from './pages/CreativeReport';
import ComprehensiveCreativeReport from './pages/ComprehensiveCreativeReport';
import SharedCreativeReport from './pages/SharedCreativeReport';
import SharedComprehensiveReport from './pages/SharedComprehensiveReport';
import SharedTrendDetail from './pages/SharedTrendDetail';
import SlackInstall from './pages/SlackInstall';
import TrendAnalytics from './pages/TrendAnalytics';
import TrendSpotter from './pages/TrendSpotter';
import TrendDiscovery from './pages/TrendDiscovery';
import SocialProfile from './pages/SocialProfile';
import VideoInspirationLibrary from './pages/VideoInspirationLibrary';
import Payment from './pages/Payment';
import AdminDashboard from './pages/AdminDashboard';
import AdminCRM from './pages/AdminCRM';
import AdminBriefDigestReviews from './pages/AdminBriefDigestReviews';
import AdminDigestAds from './pages/AdminDigestAds';
import TrendVideoSocialPage from './pages/TrendVideoSocialPage';
import WeeklyTrendDigest from './pages/WeeklyTrendDigest';
import DigestPreferences from './pages/DigestPreferences';
import BriefDigestFeedback from './pages/BriefDigestFeedback';
import BriefDigestPreferences from './pages/BriefDigestPreferences';
import TrendScanPage from './pages/TrendScanPage';
import MediaLibrary from './components/MediaLibrary';
import CustomLabeling from './components/CustomLabeling';
import Layout from './components/Layout';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { ThemeProvider } from './contexts/ThemeContext';
import './App.css';

// Protected Route Component (just checks authentication)
const ProtectedRoute = ({ children }) => {
  const { isAuthenticated, loading } = useAuth();
  const location = useLocation();
  
  // Show loading while authentication is being checked
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-purple-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading...</p>
        </div>
      </div>
    );
  }
  
  // Preserve the current location (pathname + search) when redirecting to login
  return isAuthenticated ? children : <Navigate to="/login" state={{ from: location }} replace />;
};

// Account Required Route Component (checks authentication AND account selection)
const AccountRequiredRoute = ({ children }) => {
  const { isAuthenticated, selectedAccountId, loading } = useAuth();
  
  // Show loading while authentication is being checked
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-purple-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading...</p>
        </div>
      </div>
    );
  }
  
  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }
  
  if (!selectedAccountId) {
    return <Navigate to="/select-account" replace />;
  }
  
  return children;
};

// Main App Component
function AppContent() {
  return (
    <Router>
      <div className="App">
        <Toaster 
          position="top-right"
          toastOptions={{
            duration: 4000,
            style: {
              background: '#363636',
              color: '#fff',
            },
          }}
        />
        
        <Routes>
          <Route path="/login" element={<Login />} />
          
          <Route path="/select-account" element={
            <ProtectedRoute>
              <AccountSelection />
            </ProtectedRoute>
          } />
          
          <Route path="/" element={<Navigate to="/trend-spotter" replace />} />
          
          <Route path="/dashboard" element={
            <AccountRequiredRoute>
              <Dashboard />
            </AccountRequiredRoute>
          } />
          
          <Route path="/chat" element={
            <AccountRequiredRoute>
              <Layout>
                <Chat />
              </Layout>
            </AccountRequiredRoute>
          } />
          
          <Route path="/creative-report" element={
            <AccountRequiredRoute>
              <CreativeReport />
            </AccountRequiredRoute>
          } />
          
          <Route path="/comprehensive-creative-report" element={
            <AccountRequiredRoute>
              <ComprehensiveCreativeReport />
            </AccountRequiredRoute>
          } />
          
          <Route path="/media-library" element={
            <AccountRequiredRoute>
              <Layout>
                <MediaLibrary />
              </Layout>
            </AccountRequiredRoute>
          } />
          
          <Route path="/custom-labeling" element={
            <AccountRequiredRoute>
              <Layout>
                <CustomLabeling />
              </Layout>
            </AccountRequiredRoute>
          } />
          
          <Route path="/trend-analytics" element={
            <AccountRequiredRoute>
              <Layout>
                <TrendAnalytics />
              </Layout>
            </AccountRequiredRoute>
          } />
          
          {/* Trend Spotter route - requires authentication but not account selection */}
          <Route path="/trend-spotter" element={
            <ProtectedRoute>
              <TrendSpotter />
            </ProtectedRoute>
          } />
          
          {/* Trend Discovery route - requires authentication but not account selection */}
          <Route path="/trend-discovery" element={
            <ProtectedRoute>
              <TrendDiscovery />
            </ProtectedRoute>
          } />
          
          {/* Video Inspiration Library route - requires authentication but not account selection */}
          <Route path="/video-inspiration-library" element={
            <ProtectedRoute>
              <VideoInspirationLibrary />
            </ProtectedRoute>
          } />

          {/* Hidden Social Profile - no nav link, URL only */}
          <Route path="/social-profile" element={
            <ProtectedRoute>
              <SocialProfile />
            </ProtectedRoute>
          } />
          
          {/* Payment page - requires authentication */}
          <Route path="/payment" element={
            <ProtectedRoute>
              <Payment />
            </ProtectedRoute>
          } />
          
          {/* Admin Dashboard - requires authentication and admin access */}
          <Route path="/admin" element={
            <ProtectedRoute>
              <AdminDashboard />
            </ProtectedRoute>
          } />
          <Route path="/admin/crm" element={
            <ProtectedRoute>
              <AdminCRM />
            </ProtectedRoute>
          } />
          <Route path="/admin/brief-digest-reviews" element={
            <ProtectedRoute>
              <AdminBriefDigestReviews />
            </ProtectedRoute>
          } />
          <Route path="/admin/digest-ads" element={
            <ProtectedRoute>
              <AdminDigestAds />
            </ProtectedRoute>
          } />
          <Route path="/admin/trend-video-social" element={
            <ProtectedRoute>
              <TrendVideoSocialPage />
            </ProtectedRoute>
          } />
          
          {/* Weekly Trend Digest - requires authentication */}
          <Route path="/weekly-digest" element={
            <ProtectedRoute>
              <WeeklyTrendDigest />
            </ProtectedRoute>
          } />
          
          {/* Digest subscribe/unsubscribe - no auth, token in query */}
          <Route path="/digest-preferences" element={<DigestPreferences />} />
          <Route path="/brief-digest-feedback" element={<BriefDigestFeedback />} />
          <Route path="/brief-digest-preferences" element={<BriefDigestPreferences />} />
          {/* Public shared report route - no authentication required */}
          <Route path="/shared-report/:reportId" element={<SharedCreativeReport />} />
          {/* Public shared trend detail - no authentication required */}
          <Route path="/share/trend/:trendId" element={<SharedTrendDetail />} />
          {/* Slack install landing page - instructions and Add to Slack (no auth required to view) */}
          <Route path="/slack-install" element={<SlackInstall />} />
          
          {/* Public shared comprehensive report route - no authentication required */}
          <Route path="/shared-comprehensive-report/:accountName/:dateRange/:timestamp" element={<SharedComprehensiveReport />} />
        <Route path="/shared-comprehensive-report/:reportId" element={<SharedComprehensiveReport />} />
          
          <Route path="/trend-scan" element={
            <ProtectedRoute>
              <TrendScanPage />
            </ProtectedRoute>
          } />

          <Route path="*" element={<Navigate to="/trend-spotter" replace />} />
        </Routes>
      </div>
    </Router>
  );
}

// App with Auth Provider and Theme Provider
function App() {
  return (
    <ThemeProvider>
      <AuthProvider>
        <AppContent />
      </AuthProvider>
    </ThemeProvider>
  );
}

export default App; 